import { ReactNode, useEffect, useMemo, useRef } from 'react'
import {
  AtendimentoType,
  formatDateToPtBr,
  initialQueryState,
  OptionsHelper,
  Permission,
  useDebounce,
  validateDateRange,
  VinculoType,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { Params } from '../../core/_params'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useAuth } from '../../../../../auth'
import { isVinculoMedico } from 'app/modules/auth/core/vinculos'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Destaca o período como obrigatório (ex.: tentou exportar sem período). */
  forceDateRangeError?: boolean
  /** Conteúdo à direita da linha dos campos (ex.: exportar CSV). */
  actions?: ReactNode
}

export const AtendimentoGestaoListSearchComponent = ({ forceDateRangeError, actions }: Props) => {
  const { currentUser } = useAuth()
  const { updateState } = useQueryRequest<Params>()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const optionsTiposAtendimento = useMemo(
    () => OptionsHelper.getOptionsTiposAtendimentoUsuario(currentUser),
    [currentUser]
  )

  // Período: no máximo 31 dias; os limites dos inputs acompanham a outra data.
  const { error: dateError, limits: dateLimits } = useMemo(
    () => validateDateRange(filters.inicio, filters.fim, { maxRangeDays: 31 }),
    [filters.inicio, filters.fim]
  )

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(() => {
    const { error } = validateDateRange(debouncedFilters.inicio, debouncedFilters.fim)
    const { range, inicio, fim } = debouncedFilters

    return [
      debouncedFilters.retorno ? `retorno:${debouncedFilters.retorno.value}` : '',
      debouncedFilters.empresa ? `paciente.empresa:${debouncedFilters.empresa.value}` : '',
      debouncedFilters.tipoAtendimento ? `tipo:${debouncedFilters.tipoAtendimento.value}` : '',
      debouncedFilters.especialidade ? `especialidade:${debouncedFilters.especialidade.value}` : '',
      debouncedFilters.protocolo ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
      debouncedFilters.paciente ? `paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*` : '',
      debouncedFilters.unidade ? `unidade.id:${debouncedFilters.unidade.value}` : '',
      debouncedFilters.vinculo ? `responsavel.vinculo:${debouncedFilters.vinculo.value}` : '',
      !error && range && inicio && fim ? `${range.value}>${inicio},${range.value}<${fim}` : '',
    ]
      .filter(Boolean)
      .join(',')
  }, [debouncedFilters])

  // `params` alimenta a exportação CSV (instituição e período). Vai por ref de propósito: o estado
  // só é atualizado quando `search` muda (período pela metade não reseta a paginação), e nesse
  // momento o efeito lê os params da mesma render — igual ao comportamento original.
  const paramsRef = useRef({
    empresa: debouncedFilters.empresa,
    range: debouncedFilters.range?.value,
    inicio: debouncedFilters.inicio,
    fim: debouncedFilters.fim,
  })
  paramsRef.current = {
    empresa: debouncedFilters.empresa,
    range: debouncedFilters.range?.value,
    inicio: debouncedFilters.inicio,
    fim: debouncedFilters.fim,
  }

  useEffect(() => {
    updateState({ ...initialQueryState, search, params: paramsRef.current })
  }, [search, updateState])

  // Unidade depende da instituição: sem instituição, não há unidade.
  useEffect(() => {
    if (!filters.empresa && filters.unidade) setFilter('unidade', undefined)
  }, [filters.empresa, filters.unidade, setFilter])

  const empresaParams = useMemo(
    () => ({ empresaId: Number(filters.empresa?.value) }),
    [filters.empresa?.value]
  )

  // Médico que não é responsável só filtra pelas próprias especialidades.
  const grupo = currentUser?.grupo?.nome
  const vinculos = currentUser?.vinculos ?? []
  const showAllEspecialidades =
    grupo === Permission.Admin ||
    grupo === Permission.SuperAdmin ||
    vinculos.some((v) => !isVinculoMedico(v)) ||
    vinculos.some((v) => isVinculoMedico(v) && v.responsavel)

  // Período começado e incompleto (ou exportação sem período): destaca o que falta.
  const periodoIniciado = !!(filters.range || filters.inicio || filters.fim || forceDateRangeError)
  const periodoCompleto = !!(filters.range && filters.inicio && filters.fim)
  const faltando = (campo: unknown) => periodoIniciado && !periodoCompleto && !campo

  // Um erro por data: o que falta preencher tem prioridade sobre o intervalo inválido.
  const erroInicio = faltando(filters.inicio)
    ? 'Selecione uma data inicial'
    : dateError.includes('inicial')
    ? dateError
    : undefined
  const erroFim = faltando(filters.fim)
    ? 'Selecione uma data final'
    : dateError && !dateError.includes('inicial')
    ? dateError
    : undefined

  const limparPeriodo = () => {
    setFilter('range', undefined)
    setFilter('inicio', '')
    setFilter('fim', '')
  }

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo (o período sai inteiro).
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    periodoCompleto && {
      key: 'periodo',
      label: filters.range!.label,
      value: `${formatDateToPtBr(filters.inicio)} a ${formatDateToPtBr(filters.fim)}`,
      onRemove: limparPeriodo,
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.unidade && {
      key: 'unidade',
      label: 'Unidade/Setor',
      value: filters.unidade.label,
      onRemove: () => setFilter('unidade', undefined),
    },
    filters.especialidade && {
      key: 'especialidade',
      label: t('especialidade.label'),
      value: filters.especialidade.label,
      onRemove: () => setFilter('especialidade', undefined),
    },
    filters.retorno && {
      key: 'retorno',
      label: 'Retorno',
      value: filters.retorno.label,
      onRemove: () => setFilter('retorno', undefined),
    },
    filters.tipoAtendimento && {
      key: 'tipoAtendimento',
      label: t('tipoAtendimento.label'),
      value: filters.tipoAtendimento.label,
      onRemove: () => setFilter('tipoAtendimento', undefined),
    },
    filters.vinculo && {
      key: 'vinculo',
      label: 'Vínculo',
      value: filters.vinculo.label,
      onRemove: () => setFilter('vinculo', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [
    filters.protocolo.trim(),
    filters.empresa,
    filters.unidade,
    filters.especialidade,
    filters.retorno,
    filters.tipoAtendimento,
    filters.vinculo,
  ].filter(Boolean).length

  return (
    <TableFilters
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label={t('cols.protocolo')}>
            <Input
              placeholder='Digite o protocolo'
              value={filters.protocolo}
              onChange={(e) => setFilter('protocolo', e.target.value)}
            />
          </TableFilter>
          <TableFilter label={t('empresa.label')}>
            <CustomSelect
              entity='empresa'
              onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEmpresas}
              value={filters.empresa}
              autoSelectSingle
            />
          </TableFilter>
          <TableFilter label='Unidade/Setor'>
            <CustomSelect
              placeholder={
                filters.empresa
                  ? 'Selecione uma unidade'
                  : `Selecione a ${t('empresa.label.lowercase')}`
              }
              onChange={(unidade?: IOption<number>) => setFilter('unidade', unidade ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsUnidades}
              fetchOptionsParams={empresaParams}
              value={filters.unidade}
              isDisabled={!filters.empresa}
            />
          </TableFilter>
          <TableFilter label={t('especialidade.label')}>
            <CustomSelect
              placeholder='Selecione uma especialidade'
              onChange={(especialidade?: IOption<number>) =>
                setFilter('especialidade', especialidade ?? undefined)
              }
              fetchOptions={
                showAllEspecialidades
                  ? OptionsHelper.getOptionsEspecialidades
                  : OptionsHelper.getOptionsEspecialidadesByProfissional
              }
              value={filters.especialidade}
            />
          </TableFilter>
          <TableFilter label='Consulta de retorno?'>
            <CustomSelect
              placeholder='Selecione'
              options={OptionsHelper.booleanOptions}
              onChange={(retorno?: IOption<boolean>) => setFilter('retorno', retorno ?? undefined)}
              value={filters.retorno}
            />
          </TableFilter>
          <TableFilter label={t('tipoAtendimento.label')}>
            <CustomSelect
              placeholder='Selecione um tipo'
              options={optionsTiposAtendimento}
              onChange={(tipo?: IOption<AtendimentoType>) =>
                setFilter('tipoAtendimento', tipo ?? undefined)
              }
              value={filters.tipoAtendimento}
            />
          </TableFilter>
          <TableFilter label='Vínculo'>
            <CustomSelect
              placeholder='Selecione um vínculo'
              fetchOptions={OptionsHelper.getOptionsTiposVinculos}
              onChange={(vinculo?: IOption<VinculoType>) =>
                setFilter('vinculo', vinculo ?? undefined)
              }
              value={filters.vinculo}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'atendimento encontrado' : 'atendimentos encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter
        label='Consultas entre'
        error={faltando(filters.range) ? 'Selecione uma opção' : undefined}
      >
        <CustomSelect
          placeholder='Selecione uma opção'
          options={OptionsHelper.consultaDatasOptions}
          onChange={(range?: IOption<string>) => setFilter('range', range ?? undefined)}
          value={filters.range}
        />
      </TableFilter>
      <TableFilter label='Data inicial' error={erroInicio}>
        <DateInput
          value={filters.inicio}
          onChange={(e) => setFilter('inicio', e.target.value)}
          minDate={dateLimits.startMin}
          maxDate={dateLimits.startMax}
          isInvalid={Boolean(erroInicio)}
        />
      </TableFilter>
      <TableFilter label='Data final' error={erroFim}>
        <DateInput
          value={filters.fim}
          onChange={(e) => setFilter('fim', e.target.value)}
          minDate={dateLimits.endMin}
          maxDate={dateLimits.endMax}
          isInvalid={Boolean(erroFim)}
        />
      </TableFilter>
    </TableFilters>
  )
}
